import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { handleTrack } from '../../../../../utils/tracker';
import { If } from '../../../../Atoms/Conditionals';
import { MarktechModal } from '../../../../Atoms/Modal';
import { Questionaire } from '../../../../S/pricing/components/interactive_faq_fold/Questionaire';
import { navigationCtx } from '../../context';
import { ISection } from '../../nav-section/index';
import { INavigatorVariant } from '../../types';
import { getIsScrollButtonDisabled, handleScrollIconClick } from './utils';

export const PricingEASTabs: React.FC<INavigatorVariant> = ({
  className = '',
}) => {
  const router = useRouter();
  const { sections, activeSection, setActiveSectionCB, setJustScrolledCB } =
    navigationCtx();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const listRef = useRef<HTMLUListElement>(null);
  const tabDivRef = useRef<HTMLDivElement>(null);

  const [isDisabledScroll, setIsDisabledScroll] = useState(
    getIsScrollButtonDisabled(listRef)
  );

  const [isScrollNeededOnLoad, setIsScrollNeededOnLoad] = useState(
    !getIsScrollButtonDisabled(listRef)
  );

  useEffect(() => {
    if (!router) return;

    const { planId } = router.query;
    if (planId && typeof planId === 'string') {
      tabDivRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setJustScrolledCB(true);
      setActiveSectionCB(planId);
    }
  }, [activeSection]);

  useEffect(() => {
    setIsDisabledScroll(getIsScrollButtonDisabled(listRef));
    setIsScrollNeededOnLoad(!getIsScrollButtonDisabled(listRef));
  }, [listRef.current?.offsetWidth]);

  const handleClick = (e: React.MouseEvent | React.TouchEvent, id: string) => {
    e.preventDefault();
    router.query.planId = id;
    router.route = '/s/pricing';
    router.pathname = '/s/pricing';
    router.replace(router, undefined, { shallow: true, scroll: false });
    setJustScrolledCB(true);
    setActiveSectionCB(id);
    handleTrack({ label: 'eas-navbar', obj: e.target });
  };

  return (
    <>
      <div className="anchor" ref={tabDivRef}>
        {activeSection}
      </div>
      <nav
        className={`pricing-eas w-full flex items-center bg-white ${className}`}
      >
        <div className="w-full flex items-center justify-between border-b-sm border-b-grey-300 gap-2">
          <div className="flex w-5/6 items-center md:w-4/5 sm:w-full">
            <ul
              onScroll={() => {
                setIsDisabledScroll(getIsScrollButtonDisabled(listRef));
              }}
              ref={listRef}
              className="flex items-center overflow-x-scroll"
            >
              {sections?.map((section) => {
                const sectionProps: ISection = section.props;
                const { id, name } = sectionProps;
                return (
                  <li
                    id={id}
                    onClick={(e) => {
                      handleClick(e, id);
                    }}
                    key={id}
                    className={`text-base cursor-pointer h-14 whitespace-nowrap inline-flex items-center py-4 px-4 sm:px-2 pb-0 leading-none pt-0 ${
                      id === activeSection
                        ? 'text-font-500 font-bold border-b-md border-blue-350'
                        : 'text-font-200 font-medium'
                    }`}
                  >
                    {name}
                  </li>
                );
              })}
            </ul>
            <If condition={isScrollNeededOnLoad}>
              <button
                className={`w-8 h-8 md:w-1/12 sm:ml-4 ${
                  isDisabledScroll
                    ? 'pointer-events-none cursor-not-allowed opacity-25'
                    : ''
                } focus:outline-none sm:hidden`}
                onClick={() => handleScrollIconClick(listRef)}
              >
                <img
                  src="https://assets1.cleartax-cdn.com/cleartax/images/1682501617_rightchevron.svg"
                  alt="scroll-button"
                  className="w-8 h-8"
                  width={28}
                  height={28}
                />
              </button>
            </If>
          </div>
          <div
            className="text-blue-350 font-bold text-s-14 text-right whitespace-nowrap sm:ml-0 cursor-pointer sm:hidden"
            onClick={(e) => {
              setIsModalVisible(!isModalVisible);
              handleTrack({ label: 'sec-navbar', obj: e.target });
            }}
          >
            Recommend a plan →
          </div>
        </div>
      </nav>
      <MarktechModal
        open={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        closeIcon={'✕'}
        classNames={{
          modal:
            'qnaMaxWidth mobile-bottom-fixed mobile-search-box rounded-2xl h-fit max-h-[95vh] overflow-hidden w-full',
          closeButton: 'text-s-24',
        }}
      >
        <div className="modal rounded-xl modal-open bg-[#F9FAFB]">
          <Questionaire
            leadDetails={{
              type: 'plan-selection-form',
              sheetName: 'PlanSelectionQA',
            }}
          />
        </div>
      </MarktechModal>
    </>
  );
};
